import React, { createContext, ReactNode, useContext, useState, useEffect } from "react";
import { persistGet, persistSet } from "@/lib/utils";
import { addDays, subDays } from "date-fns";

export interface DateRange {
  from: Date;
  to: Date;
}

interface DateRangeContextType {
  dateRange: DateRange;
  compare: boolean;
  compareRange: DateRange;
  setDateRange: React.Dispatch<React.SetStateAction<DateRange>>;
  setCompare: React.Dispatch<React.SetStateAction<boolean>>;
}

const DateRangeContext = createContext<DateRangeContextType | undefined>(undefined);

export const DateRangeProvider = ({ children }: { children: ReactNode }) => {
  // Default date range
  const [dateRange, setDateRange] = useState<DateRange>({
    from: subDays(new Date(), 30),
    to: subDays(new Date(), 1),
  });
  

  const [compare, setCompare] = useState<boolean>(true);
  // Compute initial compare range: compareEnd = main.from - 1, compareStart = compareEnd - (main duration)
  const getCompareRange = (main: DateRange): DateRange => {
    const compareEnd = addDays(main.from, -1);
    const duration = main.to.getTime() - main.from.getTime();
    const compareStart = new Date(compareEnd.getTime() - duration);
    return { from: compareStart, to: compareEnd };
  };

  const [compareRange, setCompareRange] = useState<DateRange>(getCompareRange(dateRange));
  const [isHydrated, setIsHydrated] = useState(false);

  // Hydrate from localStorage once on mount
  useEffect(() => {
    const stored = persistGet<{
      dateRange?: { from: string; to: string };
      compareRange?: { from: string; to: string };
      compare?: boolean;
    }>("app_v2_prefs_dates");
    if (stored) {
      try {
        if (stored.dateRange?.from && stored.dateRange?.to) {
          const from = new Date(stored.dateRange.from);
          const to = new Date(stored.dateRange.to);
          if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
            setDateRange({ from, to });
          }
        }
        if (typeof stored.compare === 'boolean') {
          setCompare(stored.compare);
        }
        if (stored.compareRange?.from && stored.compareRange?.to) {
          const from = new Date(stored.compareRange.from);
          const to = new Date(stored.compareRange.to);
          if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
            setCompareRange({ from, to });
          }
        }
      } catch {}
    }
    setIsHydrated(true);
  }, []);

  // Auto-update compare range whenever main dateRange or compare flag changes
  useEffect(() => {
    if (compare) {
      setCompareRange(getCompareRange(dateRange));
    }
  }, [dateRange, compare]);
  
  // Persist on changes (only after initial hydration to avoid overwriting stored values)
  useEffect(() => {
    if (!isHydrated) return;
    persistSet("app_v2_prefs_dates", {
      dateRange: { from: dateRange.from.toISOString(), to: dateRange.to.toISOString() },
      compare,
      compareRange: { from: compareRange.from.toISOString(), to: compareRange.to.toISOString() }
    });
  }, [dateRange, compare, compareRange, isHydrated]);



  return (
    <DateRangeContext.Provider
      value={{ dateRange, compare, compareRange, setDateRange, setCompare }}
    >
      {children}
    </DateRangeContext.Provider>
  );
};

export const useDateRange = (): DateRangeContextType => {
  const context = useContext(DateRangeContext);
  if (!context) {
    throw new Error("useDateRange must be used within a DateRangeProvider");
  }
  return context;
};
